import React, { useState, useEffect, createContext } from 'react';
import { useLocation, BrowserRouter as Router } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingHeader from './components/LandingHeader';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { useAuthStore } from './stores';
import './i18n';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import AnalyticsTracker from './components/AnalyticsTracker';
import AppRoutes from './AppRoutes';
import { ErrorProvider } from './contexts/ErrorContext';
import { ToastProvider } from './contexts/ToastContext';

export const AuthContext = createContext(null);

const AppContent = ({ children, isAuthenticated }) => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/' && !isAuthenticated;

  return (
      <div className={`flex flex-col min-h-screen w-screen overflow-x-hidden ${isLandingPage ? 'bg-black' : 'bg-white'}`}>
        {children}
      </div>
  );
};

function App() {
  const { isAuthenticated, token, userInfo, loading, updateUserInfo } = useAuthStore();
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const { i18n } = useTranslation();

  // При загрузке компонента проверяем сохранённый язык в cookies
  useEffect(() => {
    const savedLanguage = Cookies.get('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage); // Восстанавливаем язык из cookies
    }
  }, [i18n]);

  // Сохраняем язык в cookies при его изменении
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      Cookies.set('language', lng, { expires: 7 });
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const checkAuth = async () => {
      if (token && !userInfo) {
        try {
          await updateUserInfo();
        } catch (error) {
          handleLogout();
        }
      }
    };
    checkAuth();
  }, [token, userInfo, updateUserInfo]);

  const handleLoginSuccess = async () => {
    // Token and userInfo are already set in the store by login() method
    // Just close the dialog and let the store state propagate
    setLoginOpen(false);
  };

  const handleLogout = () => {
    useAuthStore.getState().logout();
    Cookies.remove('token');
  };

  const handleOpenLogin = () => setLoginOpen(true);
  const handleCloseLogin = () => setLoginOpen(false);
  const handleOpenRegister = () => setRegisterOpen(true);
  const handleCloseRegister = () => setRegisterOpen(false);
  const handleRegisterSuccess = () => {
    setRegisterOpen(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // If authenticated but userInfo not loaded yet, show loading
  if (isAuthenticated && !userInfo) {
    return <div>Loading user info...</div>;
  }

  return (
      <ThemeProvider theme={theme}>
        <ToastProvider>
          <ErrorProvider>
            <AuthContext.Provider value={{ isAuthenticated, userInfo, token, isLoading: loading }}>
              <Router>
                <AnalyticsTracker />
                <AppContent isAuthenticated={isAuthenticated}>
                  {isAuthenticated ? (
                      <Header
                          userInfo={userInfo}
                          onLogout={handleLogout}
                          onLoginClick={handleOpenLogin}
                          onRegisterClick={handleOpenRegister}
                      />
                  ) : (
                      <LandingHeader
                          onLoginClick={handleOpenLogin}
                          onRegisterClick={handleOpenRegister}
                          onLoginSuccess={handleLoginSuccess}
                      />
                  )}
                  <div className="flex-grow">
                    <AppRoutes
                        handleOpenLogin={handleOpenLogin}
                        handleLoginSuccess={handleLoginSuccess}
                        handleLogout={handleLogout}
                        loginOpen={loginOpen}
                        handleCloseLogin={handleCloseLogin}
                        registerOpen={registerOpen}
                        handleCloseRegister={handleCloseRegister}
                        handleRegisterSuccess={handleRegisterSuccess}
                    />
                  </div>
                  <Footer />
                </AppContent>
              </Router>
            </AuthContext.Provider>
          </ErrorProvider>
        </ToastProvider>
      </ThemeProvider>
  );
}

export default App;
